import IconIcDiscordSquare from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcDiscordSquare";
import IconIcGitHubSquare from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcGitHubSquare";
import IconIcMailchimp from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcMailchimp";
import IconIcTracking from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcTracking";
import IconIcWidgetIcon from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcWidgetIcon";
import IconIcWebsiteOptIn from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcWebsiteOptIn";
import IconIcTelegramSquare from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcTelegramSquare";
import IconIcSlackSquare from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcSlackSquare";
import IconIcTelegramNotificationIcon from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcTelegramNotificationIcon";
import IconIcDiscordNotificationIcon from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcDiscordNotificationIcon";
import IconIcTelegramAnalyticsIcon from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcTelegramAnalyticsIcon";
import { isVersionLessThan } from "features/helpers/utils";
import {
  selectAnalyticsModule,
  selectAttributionVersionCanUpdate,
  selectNotificationModule,
  selectServiceModule,
  selectWidgetModule,
  selectWidgetVersionCanUpdate,
  selectWebsiteOptIn,
  selectWebsiteOptInVersionCanUpdate,
  selectEnabledEcosystems,
} from "features/metadesk/redux/entityConfig/entityConfig.selector";
import {
  useFetchEntityConfig,
  useFetchWidgetConfig,
  useFetchOptInConfig,
} from "features/metadesk/redux/hooks";
import { openWindow } from "features/metadesk/utils";
import useNavigateEntity from "hooks/useNavigateEntity";
import _, { get } from "lodash";
import axios from "axios";
import { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router";
import { apiUrl } from "features/configure";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";

export default function useGetIntegrationArr({ combinedWidgetConfig = false }) {
  const { navigateEntity } = useNavigateEntity();
  const { formatMessage } = useIntl();
  const [entityName] = useOutletContext();
  const { entityConfig, fetchEntityConfig } = useFetchEntityConfig();
  const { fetchWidgetConfig, widgetConfig, fetchWidgetConfigPending } =
    useFetchWidgetConfig();
  const { fetchOptInConfig } = useFetchOptInConfig();
  const dispatchSnackbar = useDispatchSnackbar();
  const hasService = get(entityConfig, "modules.service");
  const hasNotification = get(entityConfig, "modules.notification");
  const widgetVersion = useSelector(selectWidgetVersionCanUpdate);
  const optInVersion = useSelector(selectWebsiteOptInVersionCanUpdate);
  const trackingVersion = useSelector(selectAttributionVersionCanUpdate);
  const widgetModule = useSelector(selectWidgetModule);
  const websiteOptIn = useSelector(selectWebsiteOptIn);
  const serviceModule = useSelector(selectServiceModule);
  const notificationModule = useSelector(selectNotificationModule);
  const analyticsModule = useSelector(selectAnalyticsModule);
  const enabledEcosystems = useSelector(selectEnabledEcosystems);

  const isEvmEnabled = enabledEcosystems.includes("evm");

  // 進來時確保狀態都是最新的
  useEffect(() => {
    fetchEntityConfig({ entityName });
    fetchWidgetConfig({ entityName });
    fetchOptInConfig({ entityName });
  }, []);

  // 目前安裝的widget版本
  const {
    version: connectedWidgetVersion,
    latestVersion: widgetLatestVersion,
    canUpdate: widgetCanUpdate,
  } = widgetVersion || {};
  const {
    version: connectedOptInVersion,
    latestVersion: optInLatestVersion,
    canUpdate: optInCanUpdate,
  } = optInVersion || {};
  const {
    version: connectedAttributionVersion,
    latestVersion: attributionLatestVersion,
    canUpdate: attributionCanUpdate,
  } = trackingVersion || {};

  function getIntegrationStatus(link, canUpdate = false) {
    if (canUpdate) return "Update Available";
    if (
      _.get(serviceModule, `${link}.connected`) ||
      _.get(notificationModule, `${link}.connected`) ||
      _.get(analyticsModule, `${link}.connected`) ||
      (link === "widgetSupport" && _.get(widgetModule, `connected`)) ||
      (link === "websiteOptIn" && _.get(websiteOptIn, `connected`))
    ) {
      return "Active";
    }
    if (
      _.get(serviceModule, `${link}.connecting`) ||
      _.get(notificationModule, `${link}.connecting`) ||
      _.get(analyticsModule, `${link}.connecting`) ||
      (link === "widgetSupport" && _.get(widgetModule, `connecting`)) ||
      (link === "websiteOptIn" && _.get(websiteOptIn, `connecting`))
    ) {
      return "Setup Incomplete";
    }
    return "not-connected";
  }

  function getIntegrationEnabled(link) {
    if (
      _.get(serviceModule, `${link}.enabled`) ||
      _.get(notificationModule, `${link}.enabled`) ||
      _.get(analyticsModule, `${link}.enabled`) ||
      (link === "widgetSupport" && _.get(widgetModule, `enabled`)) ||
      (link === "websiteOptIn" && _.get(websiteOptIn, `enabled`))
    ) {
      return true;
    }
    return false;
  }

  async function fetchLinearOAuthUrl() {
    try {
      const { data } = await axios.get(
        `${apiUrl}/api/linear/${entityName}/oauth/authorize`
      );
      return data.redirectUrl;
    } catch (err) {
      dispatchSnackbar({ err });
    }
  }

  const integrationWayArr = [
    // discord
    {
      auth: hasService && getIntegrationEnabled("discord"),
      icon: <IconIcDiscordSquare width={40} height={40} />,
      title: formatMessage({ id: "integration.discordBot.name" }),
      description: formatMessage({
        id: "integration.discordBot.description",
      }),
      link: "discord",
      tags: ["Community"],
      own: true,
      setting: () => {
        navigateEntity("/settings/general/integration/update/discord");
      },
      extraAction: () => {
        const url = get(entityConfig, "serviceModule.discord.url");
        openWindow(url, true);
      },
      updatedAt: get(entityConfig, "serviceModule.discord.updatedAt"),
      status: getIntegrationStatus("discord"),
    },
    // Telegram
    {
      auth: hasService && getIntegrationEnabled("telegram"),
      icon: <IconIcTelegramSquare width={40} height={40} />,
      title: formatMessage({ id: "integration.telegramBot.name" }),
      description: formatMessage({
        id: "integration.appIntro.telegramTicketBot",
      }),
      link: "telegram",
      tags: ["Community"],
      own: true,
      setting: () => {
        navigateEntity("/settings/general/integration/update/telegram");
      },
      extraAction: () => {
        navigateEntity("/settings/customization/telegramSetting");
      },
      updatedAt: get(entityConfig, "serviceModule.telegram.updatedAt"), // 添加 updatedAt
      status: getIntegrationStatus("telegram"),
    },
    // Telegram Analytics
    {
      auth: hasService && getIntegrationEnabled("telegramAnalytics"),
      icon: <IconIcTelegramAnalyticsIcon width={40} height={40} />,
      title: formatMessage({ id: "integration.telegramAnalytics.name" }),
      description: formatMessage({
        id: "integration.appIntro.telegramAnalytics",
      }),
      link: "telegramAnalytics",
      tags: ["Community"],
      own: true,
      setting: () => {
        navigateEntity(
          "/settings/general/integration/update/telegramAnalytics"
        );
      },

      updatedAt: get(entityConfig, "serviceModule.telegramAnalytics.updatedAt"), // 添加 updatedAt
      status: getIntegrationStatus("telegramAnalytics"),
    },

    // widget
    {
      auth:
        (hasService || hasNotification) &&
        getIntegrationEnabled("widgetSupport"),
      icon: <IconIcWidgetIcon width={40} height={40} />,
      title: formatMessage({ id: "integration.widget.name" }),
      description: formatMessage({ id: "integration.widget.description" }),
      link: "widgetSupport",
      own: true,
      tags: ["Community", "Notification"],
      setting: () => {
        navigateEntity("/settings/general/integration/update/widget");
      },
      addLink: () => navigateEntity("/settings/general/integration/add/widget"),
      currentVersion: connectedWidgetVersion,
      latestVersion: widgetLatestVersion,
      canUpdate: widgetCanUpdate,
      showTooltip:
        connectedWidgetVersion &&
        connectedWidgetVersion !== "0.0.0" &&
        isVersionLessThan(connectedWidgetVersion, "1.1.0"),
      tooltipText: formatMessage({ id: "integration.apikey.version.hint" }),
      updatedAt: get(entityConfig, "serviceModule.widgetSupport.updatedAt"), // 添加 updatedAt
      status: getIntegrationStatus("widgetSupport"),
    },
    // Website Opt-In Section (Notification Subscription)
    {
      auth: hasNotification && getIntegrationEnabled("websiteOptIn"), // 什麼權限
      // auth: true,
      icon: <IconIcWebsiteOptIn width={40} height={40} />,
      title: formatMessage({ id: "integration.subscribeBtn.name" }),
      description: formatMessage({
        id: "integration.subscribeBtn.description",
      }),
      link: "websiteOptIn",
      tags: ["Notification"], // 小類型框
      setting: () => {
        // 點擊去設定頁
        navigateEntity("/settings/general/integration/update/websiteOptIn");
      },
      addLink: () =>
        navigateEntity("/settings/general/integration/add/websiteOptIn"),
      currentVersion: connectedOptInVersion,
      latestVersion: optInLatestVersion,
      canUpdate: optInCanUpdate,
      showTooltip:
        connectedOptInVersion &&
        connectedOptInVersion !== "0.0.0" &&
        isVersionLessThan(connectedOptInVersion, "1.1.0"),
      tooltipText: formatMessage({ id: "integration.apikey.version.hint" }),
      updatedAt: get(entityConfig, "notificationModule.websiteOptIn.updatedAt"), // 添加 updatedAt
      status: getIntegrationStatus("websiteOptIn"), // 是否有啟用
    },
    // xmtp
    {
      auth: hasNotification && getIntegrationEnabled("xmtp") && isEvmEnabled,
      icon: (
        <img
          src={require("assets/img/xmtp.svg").default}
          style={{ width: 40, height: 40 }}
        />
      ),
      title: formatMessage({ id: "integration.xmtp.name" }),
      description: formatMessage({ id: "integration.xmtp.description" }),
      tags: ["Notification"],
      link: "xmtp",
      setting: () => {
        navigateEntity("/settings/general/integration/update/xmtp");
      },
      updatedAt: get(entityConfig, "notificationModule.xmtp.updatedAt"),
      status: getIntegrationStatus("xmtp"),
    },

    // trackingTag
    {
      auth: analyticsModule && getIntegrationEnabled("attribution"),
      icon: <IconIcTracking width={40} height={40} />,
      title: formatMessage({ id: "integration.trackingTag.name" }),
      description: formatMessage({
        id: "integration.trackingTag.description",
      }),
      link: "attribution",
      tags: ["Footprint"],
      own: true,
      setting: () => {
        navigateEntity("/settings/general/integration/update/attribution");
      },
      currentVersion: connectedAttributionVersion,
      latestVersion: attributionLatestVersion,
      canUpdate: attributionCanUpdate,
      showTooltip:
        connectedAttributionVersion &&
        isVersionLessThan(connectedAttributionVersion, "1.1.0"),
      tooltipText: formatMessage({ id: "integration.apikey.version.hint" }),
      updatedAt: get(entityConfig, "analyticsModule.attribution.updatedAt"),
      status: getIntegrationStatus("attribution", attributionCanUpdate),
    },
    // Discord Notification
    {
      auth: hasService,
      icon: <IconIcDiscordNotificationIcon width={40} height={40} />,
      title: formatMessage({ id: "integration.discord.name" }),
      description: formatMessage({ id: "integration.discord.description" }),
      link: "discordWebhook",
      tags: ["Productivity"],
      setting: () => {
        navigateEntity("/settings/general/integration/update/discordWebhook");
      },
      status: getIntegrationStatus("discordWebhook"),
    },
    // Slack
    {
      auth: hasService,
      icon: <IconIcSlackSquare width={40} height={40} />,
      title: formatMessage({ id: "integration.slack.name" }),
      description: formatMessage({ id: "integration.slack.description" }),
      link: "slack",
      tags: ["Productivity"],
      extraAction: () => {
        const url = _.get(entityConfig, "serviceModule.slack.url");
        openWindow(url, true);
      },
      setting: () => {
        navigateEntity("/settings/general/integration/update/slack");
      },
      status: getIntegrationStatus("slack"),
      updatedAt: get(entityConfig, "serviceModule.slack.updatedAt"),
    },
    // Telegram notification
    {
      auth: hasService && getIntegrationEnabled("telegram"),
      icon: <IconIcTelegramNotificationIcon width={40} height={40} />,
      title: formatMessage({ id: "integration.telegramNotification.name" }),
      description: formatMessage({
        id: "integration.telegramNotification.description",
      }),
      link: "telegramNotification",
      tags: ["Productivity"],
      own: true,
      setting: () => {
        navigateEntity(
          "/settings/general/integration/update/telegramNotification"
        );
      },
      updatedAt: get(
        entityConfig,
        "serviceModule.telegramNotification.updatedAt"
      ),
      status: getIntegrationStatus("telegramNotification"),
    },
    // Jira
    {
      auth: hasService && getIntegrationEnabled("jira"),
      icon: (
        <img
          src={require("assets/img/jira-logo.svg").default}
          style={{ width: 40, height: 40, borderRadius: 4 }}
        />
      ),
      title: formatMessage({ id: "integration.jiraIssue.name" }),
      description: formatMessage({ id: "integration.jiraIssue.description" }),
      link: "jira",
      tags: ["Productivity"],
      setting: () => {
        navigateEntity("/settings/general/integration/update/jira");
      },
      status: getIntegrationStatus("jira"),
      updatedAt: get(entityConfig, "serviceModule.jira.updatedAt"),
    },
    // linear
    {
      auth: hasService && getIntegrationEnabled("linear"),
      icon: (
        <img
          src={require("assets/img/linear-logo.svg").default}
          style={{ width: 40, height: 40, borderRadius: 4 }}
        />
      ),
      title: formatMessage({ id: "integration.linearIssue.name" }),
      description: formatMessage({ id: "integration.linearIssue.description" }),
      link: "linear",
      tags: ["Productivity"],
      extraAction: async () => {
        const url = await fetchLinearOAuthUrl();
        openWindow(url, true);
      },
      setting: () => {
        navigateEntity("/settings/general/integration/update/linear");
      },
      status: getIntegrationStatus("linear"),
      updatedAt: get(entityConfig, "serviceModule.linear.updatedAt"),
    },
    // githubIssue
    {
      auth: hasService && getIntegrationEnabled("github"),
      icon: <IconIcGitHubSquare width={40} height={40} />,
      title: formatMessage({ id: "integration.githubIssue.name" }),
      description: formatMessage({
        id: "integration.githubIssue.description",
      }),
      link: "github",
      tags: ["Productivity"],
      setting: "",
      extraAction: () => {
        const url = get(entityConfig, "serviceModule.github.url");
        openWindow(url);
      },
      updatedAt: get(entityConfig, "serviceModule.github.updatedAt"), // 添加 updatedAt
      status: getIntegrationStatus("github"),
    },
    // mailchimp
    {
      auth: hasService && getIntegrationEnabled("mailchimp"),
      icon: <IconIcMailchimp width={40} height={40} />,
      title: formatMessage({ id: "integration.mailchimp.name" }),
      description: formatMessage({ id: "integration.mailchimp.description" }),
      link: "mailchimp",
      tags: ["Productivity"],
      setting: () => {
        navigateEntity("/settings/general/integration/update/mailchimp");
      },
      updatedAt: get(entityConfig, "serviceModule.mailchimp.updatedAt"), // 添加 updatedAt
      status: getIntegrationStatus("mailchimp"),
    },
  ];

  // 將 widgetConfig 中的每個項目格式化為 integration 的標準結構
  const widgetIntegrationArr = widgetConfig?.map((widgetItem) => {
    const canUpdate = isVersionLessThan(
      widgetItem.jsVersion,
      widgetModule.latestVersion
    );
    return {
      auth: true, // 假設所有 widget 都是有效的
      icon: <IconIcWidgetIcon width={40} height={40} />,
      title: formatMessage({ id: "integration.widget.name" }),
      description: `${formatMessage({ id: "integration.widget.label" })} ${
        widgetItem.name
      }`,
      link: `widgetSupport`,
      tags: ["Community", "Notification"],
      setting: (incomplete = false) => {
        // if (incomplete)
        //   return navigateEntity(
        //     `/settings/general/integration/add/widget/${widgetItem._id}`
        //   );
        return navigateEntity(
          `/settings/general/integration/update/widget/${widgetItem._id}`
        );
      },
      currentVersion: widgetItem.jsVersion,
      latestVersion: widgetModule.latestVersion,
      canUpdate,
      updatedAt: widgetItem.created,
      status: canUpdate
        ? "Update Available"
        : widgetItem.connected
        ? "Active"
        : "Setup Incomplete",
    };
  });

  const resultArr = useMemo(() => {
    let resultArr = integrationWayArr;
    if (combinedWidgetConfig) {
      resultArr = integrationWayArr.filter(
        (item) => item?.link !== "widgetSupport"
      );
    }

    if (!widgetConfig) return resultArr;
    return combinedWidgetConfig
      ? resultArr.concat(widgetIntegrationArr)
      : resultArr;
  }, [integrationWayArr, widgetConfig, fetchWidgetConfigPending]);

  return resultArr;
}
